import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { AppShell } from "./components/AppShell";
import { authClient } from "./lib/auth-client";
import { ConsentPage } from "./pages/ConsentPage";
import { ForgotPage } from "./pages/ForgotPage";
import { LoginPage } from "./pages/LoginPage";
import { PlanPage } from "./pages/PlanPage";
import { SignupPage } from "./pages/SignupPage";
import { TasksPage } from "./pages/TasksPage";

// Code-split the heavier / less-frequently-hit views to shrink the initial bundle.
const BoardPage = lazy(() => import("./pages/BoardPage").then((m) => ({ default: m.BoardPage })));
const MatrixPage = lazy(() => import("./pages/MatrixPage").then((m) => ({ default: m.MatrixPage })));
const StatsPage = lazy(() => import("./pages/StatsPage").then((m) => ({ default: m.StatsPage })));
const NotesPage = lazy(() => import("./pages/NotesPage").then((m) => ({ default: m.NotesPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const AcceptInvitationPage = lazy(() =>
  import("./pages/AcceptInvitationPage").then((m) => ({ default: m.AcceptInvitationPage })),
);

async function isAuthed(): Promise<boolean> {
  const { data } = await authClient.getSession();
  return Boolean(data?.session);
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async () => {
    throw redirect({ to: (await isAuthed()) ? "/app/plan" : "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot",
  component: ForgotPage,
});

// OAuth consent screen (redirected here by the OAuth Provider during a connector
// authorization). Public route — the user already has a session by this point.
const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/consent",
  component: ConsentPage,
});

// Protected layout — guards every child.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AppShell,
  beforeLoad: async () => {
    if (!(await isAuthed())) {
      throw redirect({ to: "/login" });
    }
  },
});

const appIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/app/plan" });
  },
});

const planRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "plan",
  component: PlanPage,
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "tasks",
  component: TasksPage,
});

const boardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "board",
  component: BoardPage,
});

const matrixRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "matrix",
  component: MatrixPage,
});

const notesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "notes",
  component: NotesPage,
  // `?note=<id>` deep-links to a specific note (used when promoting a jot).
  validateSearch: (search: Record<string, unknown>): { note?: string } => ({
    note: typeof search.note === "string" ? search.note : undefined,
  }),
});

const statsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "stats",
  component: StatsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "settings",
  component: SettingsPage,
});

const acceptInvitationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "accept-invitation/$id",
  component: AcceptInvitationPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  forgotRoute,
  consentRoute,
  appRoute.addChildren([
    appIndexRoute,
    planRoute,
    tasksRoute,
    boardRoute,
    matrixRoute,
    notesRoute,
    statsRoute,
    settingsRoute,
    acceptInvitationRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultNotFoundComponent: () => (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">
          Founder<span>Deck</span>
        </div>
        <div className="auth-sub">That page doesn't exist.</div>
        <Link className="btn-ghost" to="/app/plan">
          Back to Plan
        </Link>
      </div>
    </div>
  ),
  defaultErrorComponent: ({ error }) => (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="form-err">{error.message || "Something went wrong."}</div>
        <button className="btn" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </div>
  ),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
